import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { message, Form } from 'antd';
import dayjs from 'dayjs';

import QuotationService from '../../../../../../../../shared/services/quotation';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../shared/contexts';
import { WizardDataFormContext } from './WizardDataFormContext';

import {
  CalculateFinalPrizeRequestParams,
  CalculateFinalPrizeServiceResponse,
  CarQuotationCoverage,
  CarQuotationServiceResponse,
  CarReQuotationServiceResponse,
  DECISION_TYPE,
  GenerateAPQuotationServiceRequest,
  GetCommertialsConditionsResponse,
  SelectCoveragePerVehicleServiceRequest,
  SelectOption,
  Version,
} from '../../../../../../../../shared/interfaces';

export const WizardDataFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { clientSelected } = useContext(StepOneFormContext);

  const { quotationInfo, allArticleSelected } =
    useContext(QuotationInfoContext);
  const {
    setIsLoadingPaymentMethods,
    setPaymentMethods,
    setPaymentMethodsList,
    setIsErrorServices,
    paymentMethodsList,
    setCanShowSpinner,
  } = useContext(GenericDataFormContext);

  const [stepTwoForm] = Form.useForm<any>();

  const [stepThreeForm] = Form.useForm<any>();

  const [quotationForm, setQuotationForm] =
    useState<GenerateAPQuotationServiceRequest>();

  const articleHasScoring: boolean = useMemo(
    () => allArticleSelected?.ramas?.rama[0].scoring === DECISION_TYPE.YES,
    [allArticleSelected]
  );

  const [quotationResponseByCar, setQuotationResponseByCar] = useState<
    Array<{
      componentId: string;
      quotationResponse: CarQuotationServiceResponse;
    }>
  >([]);

  const [reQuotationResponseByCar, setReQuotationResponseByCar] = useState<
    Array<{
      componentId: string;
      reQuotationResponse: CarReQuotationServiceResponse;
    }>
  >([]);

  const [coverageSelectedByVehicle, setCoverageSelectedByVehicle] = useState<
    Array<{ componentId: string; coverageSelected: CarQuotationCoverage }>
  >([]);

  const [versionSelectedByVehicle, setVersionSelectedByVehicle] = useState<
    Array<{ componentId: string; versionSelected: Version }>
  >([]);

  const [finalPrize, setFinalPrize] =
    useState<CalculateFinalPrizeServiceResponse>();

  const selectCoverageQueueRef = useRef(Promise.resolve());

  const [commertialConditions, setCommertialConditions] =
    useState<GetCommertialsConditionsResponse>();

  const getCommertialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions(
        quotationInfo?.branch!
      );

      if (response.data) {
        setCommertialConditions(response.data);
      }
    } catch {}
  }, [quotationInfo, setCommertialConditions]);

  const getPaymentMethodPerVigency = useCallback(
    async (article: string) => {
      const vigencyFrom = dayjs().format('YYYYMMDD');
      const vigencyTo = dayjs().add(1, 'year').format('YYYYMMDD');

      try {
        setIsLoadingPaymentMethods(true);
        const response = await QuotationService.getPaymentPlans(
          article,
          vigencyFrom,
          vigencyTo
        );

        if (response.data?.formasDePago?.length) {
          const paymentMethods: Array<SelectOption> =
            response.data.formasDePago.map(
              ({ descripcion, codigoFormaDePago, nroPlanDePago }) => ({
                label: descripcion,
                value: `${codigoFormaDePago}-${nroPlanDePago}`,
              })
            );
          setPaymentMethodsList(response.data.formasDePago);
          setPaymentMethods(paymentMethods);
        }
      } catch {
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    },
    [setPaymentMethods, setPaymentMethodsList, setIsLoadingPaymentMethods]
  );

  const handleTabErrors = useCallback(
    (errorFields: any[], tabPrefixes: string[], tabName: string) => {
      const tabsErrors = new Set<string>();

      errorFields.forEach((fieldError: any) => {
        const errorFieldName = fieldError.name[0];

        const tabIdError = tabPrefixes
          .map((prefix) => errorFieldName.match(new RegExp(`${prefix}-(\\d+)`)))
          .filter(Boolean)
          .map((matches) => matches[1])[0];

        if (tabIdError) tabsErrors.add(tabIdError);
      });

      if (tabsErrors.size > 0) {
        const tabsErrorsArray = Array.from(tabsErrors).join(', ');
        message.error(`Campos faltantes en ${tabName} ${tabsErrorsArray}`);
      }
    },
    []
  );

  const calculateFinalPrize = useCallback(
    async (componentId: string) => {
      const paymentMethodPlan =
        (stepThreeForm?.getFieldValue('paymentMethod') as string)?.split(
          '-'
        )[1] ?? paymentMethodsList[0].nroPlanDePago;

      const epvValue = stepThreeForm.getFieldInstance('extraVariable')
        ? Number(stepThreeForm?.getFieldValue(`extraVariable`))
        : Number(commertialConditions?.porcentajeExtraPrimaVariable);

      try {
        const params: CalculateFinalPrizeRequestParams = {
          numeroCotizacion: quotationInfo?.token!,
          rama: quotationInfo?.branch!,
          planDePago: paymentMethodPlan,
          porcentajeExtraPrimaVariable: epvValue,
        };

        const response = await QuotationService.calculateFinalPrize(params);
        setFinalPrize(response.data);
        setIsErrorServices(false);
      } catch {
        setIsErrorServices(true);
      }
    },
    [
      quotationInfo,
      stepThreeForm,
      paymentMethodsList,
      setFinalPrize,
      commertialConditions,
    ]
  );

  const getCoveragesPerVehicle = useCallback(
    (componentId: string) => {
      if (reQuotationResponseByCar.length) {
        return (
          reQuotationResponseByCar.find(
            (el) => String(el.componentId) === String(componentId)
          )?.reQuotationResponse?.coberturas ?? []
        );
      }

      return (
        quotationResponseByCar.find(
          (el) => String(el.componentId) === String(componentId)
        )?.quotationResponse?.coberturas ?? []
      );
    },
    [reQuotationResponseByCar, quotationResponseByCar]
  );

  const selectCoveragePerVehicle = useCallback(
    async (coverageCode: string, componentId: string) => {
      const run = async () => {
        try {
          setCanShowSpinner(true);
          const params: SelectCoveragePerVehicleServiceRequest = {
            token: quotationInfo?.token!,
            rama: quotationInfo?.branch!,
            arse: quotationInfo?.arse!,
            componente: componentId,
            coberturaElegida: coverageCode,
          };

          await QuotationService.selectCoveragePerVehicle(params);
          await calculateFinalPrize(componentId);

          setCoverageSelectedByVehicle((prevValues) => {
            const filteredValues = prevValues.filter(
              (el) => String(el.componentId) !== String(componentId)
            );

            const coveragesPerVehicle = getCoveragesPerVehicle(componentId);

            const coverageSelected = coveragesPerVehicle?.find(
              (el) => el.codigo === coverageCode
            )!;

            return [...filteredValues, { componentId, coverageSelected }];
          });

          setIsErrorServices(false);
        } catch {
          setIsErrorServices(true);
        } finally {
          setCanShowSpinner(false);
        }
      };

      const queued = selectCoverageQueueRef.current.then(run, run);
      selectCoverageQueueRef.current = queued.then(
        () => undefined,
        () => undefined
      );
      return queued;
    },
    [
      quotationInfo,
      calculateFinalPrize,
      getCoveragesPerVehicle,
      setCoverageSelectedByVehicle,
      setCanShowSpinner,
    ]
  );

  const removeSelectedCoverageByVehicle = useCallback(
    (componentId: string) => {
      setCoverageSelectedByVehicle((prevValues) =>
        prevValues.filter((el) => String(el.componentId) !== componentId)
      );
    },
    [setCoverageSelectedByVehicle]
  );

  const removeInfoOfVehicle = useCallback(
    (componentId: string) => {
      if (
        reQuotationResponseByCar.some((el) => el.componentId === componentId)
      ) {
        setReQuotationResponseByCar((prevValues) =>
          prevValues.filter((el) => el.componentId !== componentId)
        );
      }

      if (quotationResponseByCar.some((el) => el.componentId === componentId)) {
        setQuotationResponseByCar((prevValues) =>
          prevValues.filter((el) => el.componentId !== componentId)
        );
      }

      if (
        coverageSelectedByVehicle.some((el) => el.componentId === componentId)
      ) {
        setCoverageSelectedByVehicle((prevValues) =>
          prevValues.filter((el) => el.componentId !== componentId)
        );
      }

      if (
        versionSelectedByVehicle.some((el) => el.componentId === componentId)
      ) {
        setVersionSelectedByVehicle((prevValues) =>
          prevValues.filter((el) => el.componentId !== componentId)
        );
      }
    },
    [
      quotationResponseByCar,
      reQuotationResponseByCar,
      setQuotationResponseByCar,
      setReQuotationResponseByCar,
      coverageSelectedByVehicle,
      setCoverageSelectedByVehicle,
      versionSelectedByVehicle,
      setVersionSelectedByVehicle,
    ]
  );

  useEffect(() => {
    if (clientSelected) {
      setQuotationForm({
        ...quotationForm!,
        cliente: clientSelected,
      });
    }
    /* eslint-disable-next-line */
  }, [clientSelected]);

  useEffect(() => {
    if (quotationInfo) {
      setQuotationForm({
        token: quotationInfo.token!,
        rama: quotationInfo.branch,
        articuloRamaSecuencia: quotationInfo.arse,
      });
    }
  }, [quotationInfo]);

  useEffect(() => {
    getCommertialsConditions();
  }, [getCommertialsConditions]);

  const values = useMemo(
    () => ({
      articleHasScoring,
      calculateFinalPrize,
      coverageSelectedByVehicle,
      finalPrize,
      getCoveragesPerVehicle,
      handleTabErrors,
      quotationForm,
      quotationResponseByCar,
      removeSelectedCoverageByVehicle,
      reQuotationResponseByCar,
      selectCoveragePerVehicle,
      setCoverageSelectedByVehicle,
      setFinalPrize,
      setQuotationForm,
      setQuotationResponseByCar,
      setReQuotationResponseByCar,
      stepThreeForm,
      stepTwoForm,
      removeInfoOfVehicle,
      versionSelectedByVehicle,
      setVersionSelectedByVehicle,
      commertialConditions,
    }),
    [
      articleHasScoring,
      calculateFinalPrize,
      coverageSelectedByVehicle,
      finalPrize,
      getCoveragesPerVehicle,
      handleTabErrors,
      quotationForm,
      quotationResponseByCar,
      removeSelectedCoverageByVehicle,
      reQuotationResponseByCar,
      selectCoveragePerVehicle,
      setCoverageSelectedByVehicle,
      setFinalPrize,
      setQuotationForm,
      setQuotationResponseByCar,
      setReQuotationResponseByCar,
      stepThreeForm,
      stepTwoForm,
      removeInfoOfVehicle,
      versionSelectedByVehicle,
      setVersionSelectedByVehicle,
      commertialConditions,
    ]
  );

  useEffect(() => {
    if (quotationInfo?.article) {
      getPaymentMethodPerVigency(quotationInfo?.article);
    }
    /* eslint-disable-next-line */
  }, [quotationInfo, getPaymentMethodPerVigency]);

  return (
    <WizardDataFormContext.Provider value={values}>
      {children}
    </WizardDataFormContext.Provider>
  );
};
